import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Check, Mail, Phone, MapPin, Globe } from "lucide-react";
import { Overline } from "../components/ui-regera/Section";
import api from "../lib/api";
import { CONTACT } from "../data/site";
import { useServices } from "../lib/hooks";
import { contactAreas } from "../data/catalog";

const Field = ({ label, children, required, optional, error }) => (
  <label className="block">
    <span className="overline">
      {label} {required && <span className="text-[#e60000]">*</span>}
      {optional && <span className="text-white/40 normal-case tracking-normal">(optional)</span>}
    </span>
    <div className="mt-2">{children}</div>
    {error && <span className="text-[#e60000] text-xs mt-1 block">{error}</span>}
  </label>
);

const inputCls =
  "w-full bg-transparent border-b border-white/20 focus:border-[#e60000] py-3 text-white placeholder:text-white/30 outline-none transition-colors";

// `website` is a honeypot: hidden from people, so only bots fill it in. The server discards those.
const EMPTY = { name: "", organisation: "", email: "", phone: "", help_with: "", message: "", website: "" };

export default function Contact() {
  const [params] = useSearchParams();
  const preset = params.get("area");
  const services = useServices();
  const areas = useMemo(() => contactAreas(services), [services]);
  const [form, setForm] = useState(() => ({ ...EMPTY, help_with: areas.includes(preset) ? preset : "" }));
  useEffect(() => {
    if (areas.includes(preset)) setForm((f) => ({ ...f, help_with: preset }));
  }, [preset, areas]);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    document.title = "Contact — REGERA";
  }, []);

  const set = (k) => (e) => {
    setForm((f) => ({ ...f, [k]: e.target.value }));
    setErrors((er) => ({ ...er, [k]: undefined }));
  };

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Please enter your name.";
    if (!form.organisation.trim()) e.organisation = "Please enter your organisation.";
    if (!form.email.trim()) e.email = "Please enter your business email.";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = "Please enter a valid email address.";
    if (!form.help_with) e.help_with = "Please select an area of interest.";
    if (!form.message.trim()) e.message = "Please briefly describe your requirement.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    setFailed(false);
    if (!validate()) {
      toast.error("Please complete the highlighted fields.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/contact", form);
      setDone(true);
      toast.success("Thank you. Your enquiry has been received.");
    } catch (err) {
      setFailed(true);
      const detail = err.response?.data?.detail;
      toast.error(err.response?.status === 429 && typeof detail === "string" ? detail : "Your enquiry could not be sent. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <section id="enquiry" data-frame="contact-form" data-own-scroll data-lenis-prevent className="rg-contact-frame">
        <div className="mx-auto max-w-[1600px] px-5 sm:px-8 lg:px-12 grid lg:grid-cols-12 gap-10 lg:gap-16">
          <div className="lg:col-span-5">
            <Overline>Start a conversation</Overline>
            <h1 className="mt-6 font-display font-black text-white text-4xl sm:text-5xl xl:text-6xl leading-[1.02] tracking-tight">
              Let's discuss what you need.
            </h1>
            <p className="mt-6 text-white/70 text-base lg:text-lg leading-relaxed max-w-xl">
              Whether you are exploring a technology solution, seeking advisory support, developing a product or planning a
              transformation, tell us what you have in mind.
            </p>
          </div>

          <div className="lg:col-span-7">
            {done ? (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                data-testid="contact-success"
                role="status"
                className="border border-[#e60000]/40 bg-[#e60000]/5 p-10 lg:p-12 text-center"
              >
                <div className="mx-auto h-14 w-14 bg-[#e60000] flex items-center justify-center mb-6">
                  <Check className="h-7 w-7 text-white" />
                </div>
                <h2 className="font-display text-3xl font-black text-white">Thank you. Your enquiry has been received.</h2>
                <p className="mt-4 text-white/70 max-w-md mx-auto">
                  A member of the REGERA team will review your requirement and respond to {form.email}.
                </p>
              </motion.div>
            ) : (
              <form onSubmit={submit} noValidate data-testid="contact-form" className="relative space-y-8">
                <div className="grid sm:grid-cols-2 gap-x-8 gap-y-7">
                  <Field label="Name" required error={errors.name}>
                    <input data-testid="contact-name" value={form.name} onChange={set("name")} className={inputCls} placeholder="Your full name" autoComplete="name" />
                  </Field>
                  <Field label="Organisation" required error={errors.organisation}>
                    <input data-testid="contact-organisation" value={form.organisation} onChange={set("organisation")} className={inputCls} placeholder="Company or institution" autoComplete="organization" />
                  </Field>
                  <Field label="Business email" required error={errors.email}>
                    <input data-testid="contact-email" type="email" value={form.email} onChange={set("email")} className={inputCls} placeholder="name@organisation.com" autoComplete="email" />
                  </Field>
                  <Field label="Contact number" optional>
                    <input data-testid="contact-phone" type="tel" value={form.phone} onChange={set("phone")} className={inputCls} placeholder="+971 00 000 0000" autoComplete="tel" />
                  </Field>
                </div>
                <Field label="Area of interest" required error={errors.help_with}>
                  <select data-testid="contact-area" value={form.help_with} onChange={set("help_with")} className={`${inputCls} appearance-none cursor-pointer`}>
                    <option value="" className="bg-[#141414]">Select an area of interest</option>
                    {areas.map((o) => (
                      <option key={o} value={o} className="bg-[#141414]">{o}</option>
                    ))}
                  </select>
                </Field>
                <Field label="Brief description of your requirement" required error={errors.message}>
                  <textarea data-testid="contact-message" value={form.message} onChange={set("message")} rows={4} className={`${inputCls} resize-none`} placeholder="Tell us about the challenge, requirement or idea." />
                </Field>
                {failed && (
                  <p role="alert" data-testid="contact-error" className="text-sm text-white border-l-2 border-[#e60000] pl-4">
                    Your enquiry could not be sent. Please try again, or email us at{" "}
                    <a href={`mailto:${CONTACT.email}`} className="text-[#e60000] underline">{CONTACT.email}</a>.
                  </p>
                )}
                <button
                  type="submit"
                  data-testid="contact-submit"
                  disabled={submitting}
                  className="inline-flex items-center gap-2 bg-[#e60000] text-white px-9 py-4 text-sm tracking-[0.14em] uppercase font-medium hover:bg-[#990000] transition-colors disabled:opacity-60"
                >
                  {submitting ? "Sending…" : "Submit Enquiry"}
                </button>
                <div aria-hidden="true" className="absolute -left-[10000px] top-0 h-px w-px overflow-hidden">
                  <label>
                    Website
                    <input type="text" name="website" tabIndex={-1} autoComplete="off" value={form.website} onChange={set("website")} data-testid="contact-website" />
                  </label>
                </div>
              </form>
            )}
          </div>
        </div>
      </section>

      <section data-frame="contact-info" className="rg-sframe border-t border-white/10">
        <div className="mx-auto max-w-[1600px] w-full px-5 sm:px-8 lg:px-12 grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          <div className="lg:col-span-5">
            <Overline>Contact information</Overline>
            <h2 className="mt-6 font-display font-black text-white text-4xl sm:text-5xl leading-[1.05] tracking-tight rg-fs-display">Connect with REGERA.</h2>
          </div>
          <ul className="lg:col-span-7 space-y-6 text-white/85 text-base lg:text-lg rg-fs-lead" data-testid="contact-details">
            <li className="flex items-start gap-4">
              <Mail className="h-5 w-5 text-[#e60000] mt-1 shrink-0" />
              <a href={`mailto:${CONTACT.email}`} className="hover:text-[#e60000] transition-colors">{CONTACT.email}</a>
            </li>
            <li className="flex items-start gap-4">
              <Phone className="h-5 w-5 text-[#e60000] mt-1 shrink-0" />
              <a href={`tel:${CONTACT.phoneRaw}`} className="hover:text-[#e60000] transition-colors">{CONTACT.phone}</a>
            </li>
            <li className="flex items-start gap-4">
              <Globe className="h-5 w-5 text-[#e60000] mt-1 shrink-0" />
              <a href={`https://${CONTACT.web.replace(/^https?:\/\//, "")}`} target="_blank" rel="noreferrer" className="hover:text-[#e60000] transition-colors">{CONTACT.web}</a>
            </li>
            <li className="flex items-start gap-4">
              <MapPin className="h-5 w-5 text-[#e60000] mt-1 shrink-0" />
              <span>{CONTACT.address}</span>
            </li>
            <li className="pt-6">
              <p className="font-display text-2xl lg:text-3xl font-bold text-white leading-snug border-l-2 border-[#e60000] pl-6 rg-fs-sub">
                Tell us the challenge. Let's explore the solution.
              </p>
            </li>
          </ul>
        </div>
      </section>
    </div>
  );
}
